import * as Cesium from 'cesium';
import {
  getColorMultiplierFromSensitivity,
  getTrendColorFromDelta,
  getTrendColorFromScale,
  resolveTrendColorSchemeId
} from '../trendColorScheme.js';

const DEFAULT_GLIDER_MODEL = Object.freeze({
  key: 'ask21',
  uri: '/models/ask21.glb',
  scale: 2.0,
  minimumPixelSize: 96,
  maximumScale: 280,
  headingOffsetDeg: 90,
  labelOffsetY: -68,
  colorBlendAmount: 0.3,
  maxBankDeg: 45,
  silhouetteSize: 1.15
});

/**
 * Manages aircraft entities in the 3D Cesium viewer
 */
export class AircraftManager3D {
  constructor(viewer) {
    this.viewer = viewer;
    this.entities = new Map(); // cookie -> entity
    this.hiddenCookies = new Set(); // Track hidden aircraft for visibility toggle
    this.colorScheme = 'aircraft'; // 'aircraft', 'altitude', 'speed', 'vario', 'vario_avg', 'vario_kalman', 'vario_te', 'etf_delta', 'etf_delta_b'
    this.colorSensitivity = 0; // -100 to +100, 0 = default (no change)
    this.trendColorScheme = 'gain_loss'; // Shared ETF/XC trend palette id
    this.infoLabel = 'altitude'; // 'id', 'speed', 'altitude', 'vario', 'vario_avg', 'vario_kalman', 'vario_te', 'etf_delta', 'etf_delta_b', 'ddh'
    this.units = 'imperial'; // 'imperial' or 'metric'
    this.followingCookie = null;
    this.followCallback = null;

    // Label size configuration (3D only)
    this.cnLabelSize = 16; // Size in pixels for label

    // Per-aircraft smoothing state for vario 30s EMA and Kalman
    this.state = new Map(); // cookie -> { varioAvg, varioKalman: {x,p}, varioTE, prevSpeed, lastVarioUpdate, lastSpeedUpdate }
    this.varioEmaAlpha = 2 / (30 + 1); // EMA for ~30s window at ~1Hz
    this.kalmanQ = 0.01; // process noise
    this.kalmanR = 0.5;  // measurement noise

    // Total Energy compensation constants
    this.GRAVITY = 9.81; // m/s^2
    // TE tuning parameters
    this.teGain = 1.0; // Multiplier on TE compensation term
    this.teAccelLimit = 3.0; // Clamp dv/dt (m/s^2) to reduce noise/overcompensation
  }

  /**
   * Set color sensitivity (-100 to +100)
   * 0 = no change (default gradient)
   * -100 = minimum sensitivity (colors change more slowly)
   * +100 = maximum sensitivity (colors reach extremes faster)
   */
  setColorSensitivity(sensitivity) {
    this.colorSensitivity = Math.max(-100, Math.min(100, sensitivity || 0));
  }

  /**
   * Set ETF/XC trend color scheme id.
   */
  setTrendColorScheme(schemeId) {
    this.trendColorScheme = resolveTrendColorSchemeId(schemeId);
  }

  /**
   * Get color gradient multiplier based on sensitivity setting
   * Returns a value between 0.5 (at -100) and 2.0 (at +100)
   * Default (0) returns 1.0 (no change)
   *
   * Slider: Left = less sensitive (dampened), Right = more sensitive (amplified)
   */
  getColorMultiplier() {
    return getColorMultiplierFromSensitivity(this.colorSensitivity);
  }

  /** Normalize field names coming from UI (synonyms). */
  normalizeField(name) {
    if (!name) return name;
    const n = String(name).toLowerCase();
    if (n === 'off') return 'id';
    if (n === 'vario30s' || n === 'vario_30s' || n === 'varioavg' || n === 'vario-avg') return 'vario_avg';
    if (n === 'vario30sk' || n === 'vario_30sk' || n === 'variokalman' || n === 'vario-kalman') return 'vario_kalman';
    if (n === 'variote' || n === 'vario_te' || n === 'vario-te') return 'vario_te';
    if (n === 'etfdelta' || n === 'etf_delta' || n === 'etf-delta') return 'etf_delta';
    if (n === 'etfdeltab' || n === 'etf_delta_b' || n === 'etf-delta-b') return 'etf_delta_b';
    if (n === 'ddh' || n === 'finishaltdelta' || n === 'finish_alt_delta' || n === 'finish-alt-delta') return 'ddh';
    return name;
  }

  setColorScheme(scheme) {
    this.colorScheme = this.normalizeField(scheme) || scheme;
  }

  setInfoLabel(label) {
    this.infoLabel = this.normalizeField(label) || label;
  }
  
  /**
   * Update or create an aircraft entity
   */
  updateAircraft(position, baseColor = null) {
    const { cookie, lat, lon } = position;
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || cookie === undefined || cookie === null) return;

    let entity = this.entities.get(cookie);
    const altitudeMeters = this.getAltitudeMeters(position);
    const cartesianPosition = Cesium.Cartesian3.fromDegrees(lon, lat, altitudeMeters);
    const resolvedBaseColor = baseColor || entity?.__cwBaseColor || this.generateUniqueColor();
    const modelConfig = this.resolveGliderModelConfig(position);
    const color = this.getColorForAircraft(position, resolvedBaseColor);
    const label = this.getLabel(position);
    const displayName = position.id_cn || position.callsign || `#${cookie}`;
    const orientation = this.calculateOrientation(position, cartesianPosition, modelConfig);

    // Cache expensive description HTML - only rebuild if key data changed
    let description;
    if (entity?.__descriptionCache &&
        entity.__descriptionCacheKey === this._getDescriptionCacheKey(position)) {
      description = entity.__descriptionCache;
    } else {
      description = this.getDescription(position);
    }

    if (!entity) {
      entity = this.viewer.entities.add({
        id: `aircraft-${cookie}`,
        name: displayName, // This shows in the info box
        description: description, // Info box content
        position: cartesianPosition,
        orientation,
        show: !this.isHidden(cookie),

        point: {
          pixelSize: 7,
          color: color,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 1,
          scaleByDistance: new Cesium.NearFarScalar(800.0, 1.3, 2000000.0, 0.7),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          show: !modelConfig
        },

        model: modelConfig ? this.createModelGraphicsOptions(modelConfig, color) : undefined,

        label: {
          text: label,
          font: this.getLabelFont(),
          pixelOffset: new Cesium.Cartesian2(0, this.getLabelOffsetY(modelConfig)),
          fillColor: Cesium.Color.WHITE,
          outlineColor: Cesium.Color.BLACK,
          outlineWidth: 2,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          showBackground: false,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          scaleByDistance: new Cesium.NearFarScalar(1000.0, 1.1, 2500000.0, 0.8),
          pixelOffsetScaleByDistance: new Cesium.NearFarScalar(1000.0, 1.0, 2500000.0, 0.8)
        },
        
        // Store metadata and data for info box
        properties: {
          cookie: cookie,
          callsign: position.callsign || `#${cookie}`,
          baseColor: resolvedBaseColor,
          positionData: position, // Store full position data for info box
          // Add fields that Cesium's info box displays
          GLIDER: this.getGliderType(position) || 'Unknown',
          ALT: this.getAltitudeForDisplay(position),
          SPEED: this.getSpeedForDisplay(position),
          VARIO: this.getVarioForDisplay(position)
        }
      });

      entity.__cwBaseColor = resolvedBaseColor;
      entity.__cwPositionData = position;
      entity.__cwCallsign = position.callsign || `#${cookie}`;
      entity.__cwModelConfig = modelConfig;
      entity.__descriptionCache = description;
      entity.__descriptionCacheKey = this._getDescriptionCacheKey(position);
      this.entities.set(cookie, entity);
    } else {
      entity.position = cartesianPosition;
      entity.orientation = orientation;
      entity.name = displayName;
      entity.description = description;
      entity.__descriptionCache = description;
      entity.__descriptionCacheKey = this._getDescriptionCacheKey(position);
      entity.__cwBaseColor = resolvedBaseColor;
      entity.__cwPositionData = position;
      entity.__cwCallsign = position.callsign || `#${cookie}`;
      entity.__cwModelConfig = modelConfig;

      if (entity.point) {
        entity.point.color = color;
        entity.point.show = !modelConfig;
      }
      if (modelConfig) {
        if (!entity.model) {
          entity.model = this.createModelGraphicsOptions(modelConfig, color);
        } else {
          entity.model.uri = modelConfig.uri;
          entity.model.scale = modelConfig.scale;
          entity.model.minimumPixelSize = modelConfig.minimumPixelSize;
          entity.model.maximumScale = modelConfig.maximumScale;
          entity.model.color = Cesium.Color.WHITE;
          entity.model.colorBlendMode = Cesium.ColorBlendMode.MIX;
          entity.model.colorBlendAmount = modelConfig.colorBlendAmount;
          entity.model.silhouetteColor = this.getModelAccentColor(color);
          entity.model.silhouetteSize = modelConfig.silhouetteSize ?? 0;
          entity.model.show = true;
        }
      } else if (entity.model) {
        entity.model.show = false;
      }

      if (entity.label) {
        // Only update label text if it actually changed
        if (entity.label.text !== label) {
          entity.label.text = label;
        }
        // Reuse cached pixelOffset instead of allocating new Cartesian2
        const offsetY = this.getLabelOffsetY(modelConfig);
        if (!entity.__cachedLabelOffset || entity.__cachedLabelOffsetY !== offsetY) {
          entity.__cachedLabelOffset = new Cesium.Cartesian2(0, offsetY);
          entity.__cachedLabelOffsetY = offsetY;
        }
        entity.label.pixelOffset = entity.__cachedLabelOffset;
      }

      entity.properties.positionData = position;
      entity.properties.baseColor = resolvedBaseColor;
      entity.properties.GLIDER = this.getGliderType(position) || 'Unknown';
      entity.properties.ALT = this.getAltitudeForDisplay(position);
      entity.properties.SPEED = this.getSpeedForDisplay(position);
      entity.properties.VARIO = this.getVarioForDisplay(position);
    }
  }

  /**
   * Updates only the aircraft entity position for smooth interpolated movement.
   * Does NOT update labels, colors, or other properties - those are updated at 1Hz by updateAircraft().
   * @param {object} position - Position data with lat, lon, altitude, cookie
   */
  updateAircraftMarkerOnly(position) {
    const { cookie, lat, lon } = position;
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || cookie === undefined || cookie === null) return;

    const entity = this.entities.get(cookie);
    if (!entity) return; // Entity doesn't exist yet

    const altitudeMeters = this.getAltitudeMeters(position);
    const cartesianPosition = Cesium.Cartesian3.fromDegrees(lon, lat, altitudeMeters);
    entity.position = cartesianPosition;
    entity.orientation = this.calculateOrientation(position, cartesianPosition, entity.__cwModelConfig || null);
  }

  /**
   * Prepare an enriched position with vario_avg_mps, vario_kalman_mps, and vario_te_mps, updating per-aircraft state.
   */
  preparePosition(cookie, position) {
    const enriched = { ...position };
    const now = Date.now();

    let s = this.ensureAircraftState(cookie, position);

    if (position.vario_mps != null) {
      const dt = Math.max(0.01, Math.min(60, (now - (s.lastVarioUpdate || now)) / 1000));
      // EMA 30s
      s.varioAvg = this.varioEmaAlpha * position.vario_mps + (1 - this.varioEmaAlpha) * (s.varioAvg ?? position.vario_mps);
      // Kalman update
      s.varioKalman = this.kalmanUpdate(s.varioKalman, position.vario_mps, dt);
      
      // Total Energy vario calculation
      if (position.speed_mps != null && s.prevSpeed != null) {
        const speedDt = Math.max(0.01, Math.min(60, (now - (s.lastSpeedUpdate || now)) / 1000));
        s.varioTE = this.calculateTEVario(
          position.vario_mps,
          position.speed_mps,
          s.prevSpeed,
          speedDt
        );
        s.prevSpeed = position.speed_mps;
        s.lastSpeedUpdate = now;
      } else {
        // No speed data, use raw vario
        s.varioTE = position.vario_mps;
        if (position.speed_mps != null) {
          s.prevSpeed = position.speed_mps;
          s.lastSpeedUpdate = now;
        }
      }
      
      s.lastVarioUpdate = now;

      enriched.vario_avg_mps = s.varioAvg;
      enriched.vario_kalman_mps = s.varioKalman.x;
      enriched.vario_te_mps = s.varioTE;
    }

    this.state.set(cookie, s);
    return enriched;
  }

  /**
   * 1D Kalman filter update step
   */
  kalmanUpdate(state, measurement, dt) {
    let x = state.x;
    let p = state.p + this.kalmanQ * dt;
    const k = p / (p + this.kalmanR);
    x = x + k * (measurement - x);
    p = (1 - k) * p;
    return { x, p };
  }

  /**
   * Calculates Total Energy (TE) compensated vario
   * TE vario = altitude_rate + (speed × speed_rate) / g
   * This compensates for kinetic energy changes (speed changes) to show only potential energy changes
   * @param {number} vario_mps - Raw vario in m/s
   * @param {number} speed_mps - Current speed in m/s
   * @param {number} prevSpeed_mps - Previous speed in m/s
   * @param {number} dt - Time delta in seconds
   * @returns {number} TE compensated vario in m/s
   */
  calculateTEVario(vario_mps, speed_mps, prevSpeed_mps, dt) {
    if (!speed_mps || !prevSpeed_mps || dt <= 0 || dt > 10) {
      // If we don't have valid speed data or time delta, return raw vario
      return vario_mps;
    }
    
    // Calculate speed rate (acceleration in m/s^2)
    const speedRateRaw = (speed_mps - prevSpeed_mps) / dt;
    const speedRate = Math.max(-this.teAccelLimit, Math.min(this.teAccelLimit, speedRateRaw));
    
    // TE compensation: add kinetic energy change converted to potential energy
    // TE_vario = vario + (v × dv/dt) / g
    const teCompensation = (speed_mps * speedRate) / this.GRAVITY;
    const teVario = vario_mps + (this.teGain * teCompensation);
    
    return teVario;
  }

  /**
   * Set TE tuning parameters
   * @param {number} gain - Multiplier for TE compensation term (default 1.0)
   * @param {number} accelLimit - Clamp for dv/dt in m/s^2 (default 3.0)
   */
  setTEParams(gain = null, accelLimit = null) {
    if (typeof gain === 'number') this.teGain = gain;
    if (typeof accelLimit === 'number') this.teAccelLimit = accelLimit;
  }

  ensureAircraftState(cookie, position = null) {
    let s = this.state.get(cookie);
    if (s) {
      return s;
    }

    const initialVario = position?.vario_mps ?? 0;
    const initialSpeed = this.getSpeedMetersPerSecond(position);
    const initialHeading = Number.isFinite(position?.heading_deg) ? position.heading_deg : null;
    const initialTimestampMs = this.getPositionTimestampMs(position);

    s = {
      varioAvg: initialVario,
      varioKalman: { x: initialVario, p: 1.0 },
      varioTE: initialVario,
      prevSpeed: initialSpeed,
      lastVarioUpdate: Date.now(),
      lastSpeedUpdate: Date.now(),
      prevHeadingDeg: initialHeading,
      lastAttitudeTimeMs: initialTimestampMs,
      bankDeg: 0
    };
    this.state.set(cookie, s);
    return s;
  }

  getAltitudeMeters(position) {
    const { altitude_ft, alt_m, altitude_m } = position;
    if (alt_m !== undefined && alt_m !== null) {
      return alt_m;
    }
    if (altitude_m !== undefined && altitude_m !== null) {
      return altitude_m;
    }
    if (altitude_ft !== undefined && altitude_ft !== null) {
      return altitude_ft * 0.3048;
    }
    return 0;
  }

  getSpeedMetersPerSecond(position) {
    if (!position) return 0;
    if (Number.isFinite(position.speed_mps)) {
      return position.speed_mps;
    }
    if (Number.isFinite(position.speed_kt)) {
      return position.speed_kt * 0.514444;
    }
    return 0;
  }

  getPositionTimestampMs(position) {
    const timestamp = position?.timestamp;
    if (timestamp instanceof Date) {
      return timestamp.getTime();
    }
    if (timestamp) {
      const parsedMs = new Date(timestamp).getTime();
      if (Number.isFinite(parsedMs)) {
        return parsedMs;
      }
    }
    return Date.now();
  }

  normalizeAngleDeltaDegrees(angleDeg) {
    let delta = angleDeg % 360;
    if (delta > 180) {
      delta -= 360;
    } else if (delta < -180) {
      delta += 360;
    }
    return delta;
  }

  estimateBankAngleDeg(position, modelConfig = null) {
    const cookie = position?.cookie;
    if (cookie === undefined || cookie === null) {
      return 0;
    }

    const s = this.ensureAircraftState(cookie, position);
    const headingDeg = Number.isFinite(position?.heading_deg) ? position.heading_deg : s.prevHeadingDeg;
    const timestampMs = this.getPositionTimestampMs(position);
    let bankDeg = s.bankDeg ?? 0;

    if (Number.isFinite(headingDeg) && Number.isFinite(s.prevHeadingDeg)) {
      const dt = (timestampMs - (s.lastAttitudeTimeMs ?? timestampMs)) / 1000;

      if (dt >= 0.05 && dt <= 5) {
        const deltaHeadingDeg = this.normalizeAngleDeltaDegrees(headingDeg - s.prevHeadingDeg);
        const turnRateRadPerSec = Cesium.Math.toRadians(deltaHeadingDeg / dt);
        const speedMps = this.getSpeedMetersPerSecond(position) || s.prevSpeed || 0;
        const rawBankDeg = Cesium.Math.toDegrees(
          Math.atan((speedMps * turnRateRadPerSec) / this.GRAVITY)
        );
        const maxBankDeg = modelConfig?.maxBankDeg ?? 45;
        const clampedBankDeg = Cesium.Math.clamp(rawBankDeg, -maxBankDeg, maxBankDeg);
        const smoothing = 1 - Math.exp(-dt / 0.35);
        bankDeg = bankDeg + (clampedBankDeg - bankDeg) * smoothing;
      } else if (dt > 5) {
        bankDeg = 0;
      }
    }

    s.prevHeadingDeg = headingDeg;
    s.lastAttitudeTimeMs = timestampMs;
    s.bankDeg = bankDeg;
    if (Number.isFinite(position?.speed_mps) || Number.isFinite(position?.speed_kt)) {
      s.prevSpeed = this.getSpeedMetersPerSecond(position);
      s.lastSpeedUpdate = Date.now();
    }
    this.state.set(cookie, s);

    return bankDeg;
  }

  getGliderType(position) {
    return position.aircraft_type || position.aircraft || position.id_model || position.id_aircraft || null;
  }

  normalizeGliderKey(value) {
    if (!value) return '';
    return String(value)
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  resolveGliderModelConfig(position) {
    const rawType = this.getGliderType(position);
    const normalizedType = this.normalizeGliderKey(rawType);

    if (!normalizedType) {
      return { ...DEFAULT_GLIDER_MODEL, reason: 'generic-fallback' };
    }

    if (normalizedType.includes('ask21') || normalizedType.includes('schleicherask21')) {
      return { ...DEFAULT_GLIDER_MODEL, reason: 'matched-ask21' };
    }

    return { ...DEFAULT_GLIDER_MODEL, reason: 'generic-fallback' };
  }

  createModelGraphicsOptions(modelConfig, color) {
    const accentColor = this.getModelAccentColor(color);
    return {
      uri: modelConfig.uri,
      scale: modelConfig.scale,
      minimumPixelSize: modelConfig.minimumPixelSize,
      maximumScale: modelConfig.maximumScale,
      runAnimations: false,
      incrementallyLoadTextures: true,
      color: Cesium.Color.WHITE,
      colorBlendMode: Cesium.ColorBlendMode.MIX,
      colorBlendAmount: modelConfig.colorBlendAmount,
      silhouetteColor: accentColor,
      silhouetteSize: modelConfig.silhouetteSize ?? 0,
      show: true
    };
  }

  getModelAccentColor(color) {
    if (color instanceof Cesium.Color) {
      return new Cesium.Color(color.red, color.green, color.blue, 0.95);
    }
    return Cesium.Color.fromCssColorString('#475569');
  }

  getLabelOffsetY(modelConfig) {
    return modelConfig?.labelOffsetY ?? -40;
  }
  
  /**
   * Calculate orientation quaternion in the local ENU frame at the entity's world position.
   * Cesium's model pipeline handles glTF up-axis conversion; we only need a heading
   * offset for models whose nose faces the opposite axis.
   * @param {object} position - position payload with heading_deg
   * @param {Cesium.Cartesian3} worldPosition - world Cartesian position of the entity
   * @param {object|null} modelConfig - resolved model config
   */
  calculateOrientation(position, worldPosition, modelConfig = null) {
    const headingDeg = Number.isFinite(position?.heading_deg) ? position.heading_deg : 0;
    const headingOffsetDeg = modelConfig?.headingOffsetDeg ?? 0;
    const bankDeg = this.estimateBankAngleDeg(position, modelConfig);
    const hpr = new Cesium.HeadingPitchRoll(
      Cesium.Math.toRadians(headingDeg + headingOffsetDeg),
      0,
      Cesium.Math.toRadians(-bankDeg)
    );
    return Cesium.Transforms.headingPitchRollQuaternion(worldPosition, hpr);
  }
  
  /**
   * Get color for aircraft based on current color scheme
   */
  getColorForAircraft(position, baseColor) {
    // Color-direction rule for future maintainers:
    // - Altitude/Speed/Vario: lower values map red, higher values map green.
    // - XC Pace (ETA delta A/B): sign is inverted because negative means better/faster.
    switch (this.colorScheme) {
      case 'altitude':
        return this.getAltitudeColor(position);
      case 'speed':
        // Handle both speed_kt and speed_mps
        let speedKt = position.speed_kt;
        if (!speedKt && position.speed_mps) {
          speedKt = position.speed_mps * 1.94384;
        }
        return this.getSpeedColor(speedKt || 0);
      case 'vario':
        return this.getVarioColor((position.vario_mps || 0) * 1.94384); // Convert to knots
      case 'vario_avg':
        return this.getVarioColor(((position.vario_avg_mps ?? position.vario_mps) || 0) * 1.94384);
      case 'vario_kalman':
        return this.getVarioColor(((position.vario_kalman_mps ?? position.vario_mps) || 0) * 1.94384);
      case 'vario_te':
        return this.getVarioColor(((position.vario_te_mps ?? position.vario_mps) || 0) * 1.94384);
      case 'etf_delta':
        return this.getETFDeltaColor(position.etfDeltaSeconds, position.etfTrendSec);
      case 'etf_delta_b':
        return this.getETFDeltaColor(position.etfDeltaSecondsB, position.etfTrendSecB || position.etfTrendSec);
      case 'aircraft':
      default:
        return this.parseCesiumColor(baseColor || '#3b82f6');
    }
  }
  
  /**
   * Get altitude-based color using the active universal scheme.
   */
  getAltitudeColor(altFtOrPosition) {
    // Handle both direct altitude value or position object
    let altFt;
    if (typeof altFtOrPosition === 'object') {
      altFt = altFtOrPosition.altitude_ft;
      if (!altFt && altFtOrPosition.alt_m) {
        altFt = altFtOrPosition.alt_m * 3.28084; // Convert meters to feet
      }
      if (!altFt && altFtOrPosition.altitude_m) {
        altFt = altFtOrPosition.altitude_m * 3.28084; // Convert meters to feet (replay data)
      }
    } else {
      altFt = altFtOrPosition;
    }
    altFt = altFt || 0;

    const center = 5000;
    const multiplier = this.getColorMultiplier();
    const scaledAlt = center + (altFt - center) * multiplier;
    const lowSpan = center - 1000;
    const highSpan = 11000 - center;
    const scaleValue = scaledAlt <= center
      ? ((scaledAlt - center) / Math.max(1, lowSpan)) * 14
      : ((scaledAlt - center) / Math.max(1, highSpan)) * 14;
    const cssColor = getTrendColorFromScale(scaleValue, this.trendColorScheme) || 'rgb(136, 136, 136)';
    return Cesium.Color.fromCssColorString(cssColor);
  }
  
  /**
   * Get speed-based color using the active universal scheme.
   */
  getSpeedColor(speedKt) {
    const center = 100;
    const multiplier = this.getColorMultiplier();
    const scaledSpeed = center + (speedKt - center) * multiplier;
    const lowSpan = center - 70;
    const highSpan = 160 - center;
    const scaleValue = scaledSpeed <= center
      ? ((scaledSpeed - center) / Math.max(1, lowSpan)) * 14
      : ((scaledSpeed - center) / Math.max(1, highSpan)) * 14;
    const cssColor = getTrendColorFromScale(scaleValue, this.trendColorScheme) || 'rgb(136, 136, 136)';
    return Cesium.Color.fromCssColorString(cssColor);
  }
  
  /**
   * Get vario-based color using the active universal scheme.
   */
  getVarioColor(varioKt) {
    const multiplier = this.getColorMultiplier();
    const scaledVario = varioKt * multiplier;
    const cssColor = getTrendColorFromScale(scaledVario, this.trendColorScheme) || 'rgb(136, 136, 136)';
    return Cesium.Color.fromCssColorString(cssColor);
  }
  
  /**
   * ETF delta color mapping using the active trend-color scheme.
   * Scheme + sensitivity are centralized in trendColorScheme.js.
   */
  getETFDeltaColor(deltaSeconds, trendSeconds = 120) {
    const cssColor = getTrendColorFromDelta(deltaSeconds, trendSeconds, {
      multiplier: this.getColorMultiplier(),
      schemeId: this.trendColorScheme,
      // Global rule is low=red/high=green; XC deltas are inverted because negative is better.
      invertSign: true,
      nullColor: 'rgb(136, 136, 136)'
    });
    return Cesium.Color.fromCssColorString(cssColor);
  }
  
  /**
   * Interpolate between two Cesium colors
   */
  interpolateColor(color1, color2, t) {
    return new Cesium.Color(
      color1.red + (color2.red - color1.red) * t,
      color1.green + (color2.green - color1.green) * t,
      color1.blue + (color2.blue - color1.blue) * t,
      1.0
    );
  }
  
  /**
   * Parse CSS color to Cesium Color
   */
  parseCesiumColor(cssColor) {
    if (typeof cssColor === 'string') {
      if (cssColor.startsWith('#')) {
        return Cesium.Color.fromCssColorString(cssColor);
      } else if (cssColor.startsWith('rgb')) {
        return Cesium.Color.fromCssColorString(cssColor);
      } else if (cssColor.startsWith('hsl')) {
        return Cesium.Color.fromCssColorString(cssColor);
      }
    }
    return Cesium.Color.BLUE;
  }
  
  /**
   * Generate unique color for aircraft
   */
  generateUniqueColor() {
    const goldenRatio = 0.618033988749895;
    const hue = (this.entities.size * goldenRatio * 360) % 360;
    const saturation = 70 + (this.entities.size % 3) * 10;
    const lightness = 50 + (this.entities.size % 2) * 10;
    return `hsl(${Math.floor(hue)}, ${saturation}%, ${lightness}%)`;
  }
  
  /**
   * Get altitude formatted for info box display
   */
  getAltitudeForDisplay(position) {
    // Parse altitude from various sources (API returns strings!)
    let altFt = parseFloat(position.altitude_ft);
    if (!altFt || isNaN(altFt)) {
      const altM = parseFloat(position.alt_m || position.altitude_m);
      if (altM && !isNaN(altM)) {
        altFt = altM * 3.28084;
      }
    }
    if (!altFt || isNaN(altFt) || altFt === 0) return 'N/A';
    
    return this.units === 'imperial'
      ? `${Math.round(altFt)} ft`
      : `${Math.round(altFt * 0.3048)} m`;
  }
  
  /**
   * Get speed formatted for info box display
   */
  getSpeedForDisplay(position) {
    // Parse speed (API returns strings!)
    let speedKt = parseFloat(position.speed_kt);
    if (!speedKt || isNaN(speedKt)) {
      const speedMps = parseFloat(position.speed_mps);
      if (speedMps && !isNaN(speedMps)) {
        speedKt = speedMps * 1.94384;
      }
    }
    if (!speedKt || isNaN(speedKt) || speedKt === 0) return 'N/A';
    
    return this.units === 'imperial'
      ? `${Math.round(speedKt)} kts`
      : `${Math.round(speedKt * 1.852)} km/h`;
  }
  
  /**
   * Get vario formatted for info box display
   */
  getVarioForDisplay(position) {
    // Parse vario (API returns strings!)
    const varioMps = parseFloat(position.vario_mps);
    if (isNaN(varioMps) || varioMps === undefined || varioMps === null) return 'N/A';
    
    const varioKt = varioMps * 1.94384;
    return this.units === 'imperial'
      ? `${varioKt >= 0 ? '+' : ''}${varioKt.toFixed(1)} kts`
      : `${varioMps >= 0 ? '+' : ''}${varioMps.toFixed(1)} m/s`;
  }
  
  /**
   * Get description for info box
   */
  /**
   * Generate a cache key for description - only rebuild HTML if these values change
   */
  _getDescriptionCacheKey(position) {
    const alt = position.altitude_ft || position.alt_m || position.altitude_m || 0;
    const speed = position.speed_kt || position.speed_mps || 0;
    const vario = position.vario_mps || 0;
    const cn = position.id_cn || position.cn || '';
    const name = position.pilotName || position.pilot_name || position.id_fname || position.id_lname || '';
    return `${cn}|${name}|${Math.round(alt)}|${Math.round(speed)}|${vario.toFixed(1)}|${this.units}`;
  }

  getDescription(position) {
    const cn = position.id_cn || position.cn || '???';
    
    // Build name from first and last name
    const firstName = position.id_fname || '';
    const lastName = position.id_lname || '';
    const fullName =
      `${firstName} ${lastName}`.trim() ||
      position.pilotName ||
      position.pilot_name ||
      position.metadata?.pilotName ||
      cn ||
      'Unknown';
    
    // Handle altitude from multiple sources
    let altFt = position.altitude_ft;
    if (!altFt && position.alt_m) {
      altFt = position.alt_m * 3.28084;
    }
    if (!altFt && position.altitude_m) {
      altFt = position.altitude_m * 3.28084; // Replay data
    }
    altFt = altFt || 0;
    const altDisplay = this.units === 'imperial'
      ? `${Math.round(altFt)} ft`
      : `${Math.round(altFt * 0.3048)} m`;
    
    // Handle speed
    let speedKt = position.speed_kt;
    if (!speedKt && position.speed_mps) {
      speedKt = position.speed_mps * 1.94384;
    }
    speedKt = speedKt || 0;
    const speedDisplay = this.units === 'imperial'
      ? `${Math.round(speedKt)} kt`
      : `${Math.round(speedKt * 1.852)} km/h`;
    
    // Handle vario
    const varioMps = position.vario_mps || 0;
    const varioKt = varioMps * 1.94384;
    const varioDisplay = this.units === 'imperial'
      ? `${varioKt >= 0 ? '+' : ''}${varioKt.toFixed(1)} kt`
      : `${varioMps >= 0 ? '+' : ''}${varioMps.toFixed(1)} m/s`;
    
    return `
      <div style="font-family: sans-serif; font-size: 14px;">
        <div style="margin-bottom: 8px;"><strong>Name:</strong> ${fullName}</div>
        <div style="margin-bottom: 8px;"><strong>CN:</strong> ${cn}</div>
        <div style="margin-bottom: 8px;"><strong>Altitude:</strong> ${altDisplay}</div>
        <div style="margin-bottom: 8px;"><strong>Speed:</strong> ${speedDisplay}</div>
        <div><strong>Vario:</strong> ${varioDisplay}</div>
      </div>
    `;
  }
  
  /**
   * Get label text for aircraft
   * Format: CN on first line, then selectable info (altitude/speed/vario) on second line
   */
  getLabel(position) {
    // First line: Always show CN (competition number)
    const cn = position.id_cn || position.cn || '???';
    
    // Second line: Selectable info based on infoLabel setting
    let secondLine = '';
    
    switch (this.infoLabel) {
      case 'id':
        // When Info Label is Off (mapped to 'id'), 2D shows CN only
        return cn;
      case 'speed':
        // Handle both speed_kt and speed_mps
        let speedKt = position.speed_kt;
        if (!speedKt && position.speed_mps) {
          speedKt = position.speed_mps * 1.94384; // Convert m/s to knots
        }
        speedKt = speedKt || 0;
        secondLine = this.units === 'imperial' 
          ? `${Math.round(speedKt)} kt`
          : `${Math.round(speedKt * 1.852)} km/h`;
        break;
        
      case 'altitude':
        // Handle altitude from multiple sources
        let altFt = position.altitude_ft;
        if (!altFt && position.alt_m) {
          altFt = position.alt_m * 3.28084; // Convert meters to feet
        }
        if (!altFt && position.altitude_m) {
          altFt = position.altitude_m * 3.28084; // Convert meters to feet (replay data)
        }
        altFt = altFt || 0;
        secondLine = this.units === 'imperial'
          ? `${Math.round(altFt)} ft`
          : `${Math.round(altFt * 0.3048)} m`;
        break;
        
      case 'vario': {
        const vario = (position.vario_mps || 0) * 1.94384; // Convert to knots
        secondLine = this.units === 'imperial'
          ? `${vario >= 0 ? '+' : ''}${vario.toFixed(1)} kt`
          : `${vario >= 0 ? '+' : ''}${(vario * 0.51444).toFixed(1)} m/s`;
        break;
      }
      case 'vario_avg': {
        const vavg = ((position.vario_avg_mps ?? position.vario_mps) || 0) * 1.94384;
        secondLine = this.units === 'imperial'
          ? `${vavg >= 0 ? '+' : ''}${vavg.toFixed(1)} kt`
          : `${vavg >= 0 ? '+' : ''}${(vavg * 0.51444).toFixed(1)} m/s`;
        break;
      }
      case 'vario_kalman': {
        const vkal = ((position.vario_kalman_mps ?? position.vario_mps) || 0) * 1.94384;
        secondLine = this.units === 'imperial'
          ? `${vkal >= 0 ? '+' : ''}${vkal.toFixed(1)} kt`
          : `${vkal >= 0 ? '+' : ''}${(vkal * 0.51444).toFixed(1)} m/s`;
        break;
      }
      case 'vario_te': {
        const vte = ((position.vario_te_mps ?? position.vario_mps) || 0) * 1.94384;
        secondLine = this.units === 'imperial'
          ? `${vte >= 0 ? '+' : ''}${vte.toFixed(1)} kt`
          : `${vte >= 0 ? '+' : ''}${(vte * 0.51444).toFixed(1)} m/s`;
        break;
      }
      case 'etf_delta': {
        const deltaSeconds = position.etfDeltaSeconds;
        const windowSeconds = position.etfTrendSec;
        const denom = Number.isFinite(windowSeconds) ? `${Math.round(windowSeconds)}s` : '';
        const title = denom ? `XC Pace ${denom}` : 'XC Pace';
        const nowMs = Date.now();
        const showTitle = nowMs < (window.etfLabelShowUntilMs || 0);
        if (Number.isFinite(deltaSeconds)) {
          const rounded = Math.round(deltaSeconds);
          const sign = rounded > 0 ? '+' : '';
          const value = `${sign}${rounded}s`;
          secondLine = showTitle ? `${title}\n${value}` : value;
        } else {
          secondLine = showTitle ? `${title}\n--` : '--';
        }
        break;
      }
      case 'etf_delta_b': {
        const deltaSeconds = position.etfDeltaSecondsB;
        const windowSeconds = position.etfTrendSecB || position.etfTrendSec;
        let windowLabel = '';
        if (Number.isFinite(windowSeconds) && windowSeconds > 0) {
          const minutes = windowSeconds / 60;
          const roundedToTenth = Math.round(minutes * 10) / 10;
          const hasDecimal = Math.abs(roundedToTenth - Math.round(roundedToTenth)) > 1e-6;
          const minuteText = hasDecimal ? roundedToTenth.toFixed(1) : roundedToTenth.toFixed(0);
          windowLabel = `${minuteText}min`;
        }
        const title = windowLabel ? `XC Pace ${windowLabel}` : 'XC Pace';
        const nowMs = Date.now();
        const showTitle = nowMs < (window.etfLabelShowUntilMs || 0);
        if (Number.isFinite(deltaSeconds)) {
          const rounded = Math.round(deltaSeconds);
          const sign = rounded > 0 ? '+' : '';
          const value = `${sign}${rounded}s`;
          secondLine = showTitle ? `${title}\n${value}` : value;
        } else {
          secondLine = showTitle ? `${title}\n--` : '--';
        }
        break;
      }
      case 'ddh':
        secondLine = this.formatFinishAltDelta(position.finishAltDeltaM);
        break;
      default:
        return position.callsign || `#${position.cookie}`;
    }
    
    // Return CN on first line, info on second line
    return `${cn}\n${secondLine}`;
  }
  
  /**
   * Remove an aircraft entity
   */
  removeAircraft(cookie) {
    const entity = this.entities.get(cookie);
    if (entity) {
      this.viewer.entities.remove(entity);
      this.entities.delete(cookie);
    }
  }

  /**
   * Set hidden cookies and apply visibility to all aircraft entities
   * @param {Set} hiddenSet - Set of cookies that should be hidden
   */
  setHiddenCookies(hiddenSet) {
    this.hiddenCookies = new Set(hiddenSet);

    // Apply visibility to all aircraft entities
    this.entities.forEach((entity, cookie) => {
      const shouldShow = !this.hiddenCookies.has(cookie);
      entity.show = shouldShow;
    });
  }

  /**
   * Check if an aircraft is hidden
   * @param {string|number} cookie - The aircraft cookie
   * @returns {boolean} True if hidden
   */
  isHidden(cookie) {
    return this.hiddenCookies.has(cookie);
  }

  /**
   * Get aircraft count
   */
  getAircraftCount() {
    return this.entities.size;
  }
  
  /**
   * Set color scheme
   */
  setColorScheme(scheme) {
    this.colorScheme = scheme;
  }
  
  /**
   * Set info label type
   */
  setInfoLabel(label) {
    this.infoLabel = label;
  }
  
  /**
   * Set units
   */
  setUnits(units) {
    this.units = units;
    this.updateAllLabels();
  }

  formatFinishAltDelta(valueMeters) {
    if (!Number.isFinite(valueMeters)) {
      return '--';
    }

    const scaledValue = this.units === 'imperial'
      ? Math.round(valueMeters * 3.28084)
      : Math.round(valueMeters);
    const unit = this.units === 'imperial' ? 'ft' : 'm';
    const sign = scaledValue > 0 ? '+' : '';
    return `${sign}${scaledValue} ${unit}`;
  }
  
  /**
   * Set CN label size (3D only)
   * @param {number} size - Font size in pixels (12-24)
   */
  setCNLabelSize(size) {
    this.cnLabelSize = Math.max(12, Math.min(24, parseInt(size) || 16));
    // Update all existing entities
    this.updateAllLabels();
  }
  
  /**
   * Update all entity labels with new sizes
   */
  updateAllLabels() {
    this.entities.forEach((entity, cookie) => {
      if (entity.label) {
        const position = entity.__cwPositionData || {
          cookie: cookie,
          id_cn: entity.__cwCallsign,
          callsign: entity.__cwCallsign
        };
        
        // Update label text and font
        entity.label.text = this.getLabel(position);
        entity.label.font = this.getLabelFont();
        entity.label.pixelOffset = new Cesium.Cartesian2(0, this.getLabelOffsetY(entity.__cwModelConfig || null));
      }
    });
  }
  
  /**
   * Get font string for labels based on current size setting
   */
  getLabelFont() {
    return `600 ${this.cnLabelSize}px Inter, Roboto, "Segoe UI", sans-serif`;
  }
  
  /**
   * Follow an aircraft
   */
  followAircraft(cookie) {
    const entity = this.entities.get(cookie);
    if (entity) {
      this.followingCookie = cookie;
      this.viewer.trackedEntity = entity;
      
      if (this.followCallback) {
        this.followCallback(entity.__cwCallsign || entity.properties.callsign);
      }
    }
  }
  
  /**
   * Unfollow aircraft
   */
  unfollowAircraft() {
    this.followingCookie = null;
    this.viewer.trackedEntity = undefined;
    
    if (this.followCallback) {
      this.followCallback(null);
    }
  }
  
  /**
   * Set follow callback
   */
  setFollowCallback(callback) {
    this.followCallback = callback;
  }
  
  /**
   * Fit camera to show all aircraft
   */
  fitBounds(maxZoom = 10) {
    if (this.entities.size === 0) return;
    
    const entities = Array.from(this.entities.values());
    this.viewer.flyTo(entities, {
      duration: 2.0,
      offset: new Cesium.HeadingPitchRange(0, Cesium.Math.toRadians(-45), 0)
    });
  }
  
  /**
   * Clear all aircraft
   */
  clearAll() {
    this.entities.forEach((entity) => {
      this.viewer.entities.remove(entity);
    });
    this.entities.clear();
  }
}
